import React, { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Inbox, 
  Check, 
  X, 
  Edit3, 
  CornerUpLeft, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight, 
  AlertCircle, 
  Database,
  ArrowRight
} from "lucide-react";
import { importacaoFinanceiraService } from "../services/importacaoFinanceiraService";
import { formatCentavos, parseCentavos } from "../utils/moneyUtils";

export default function ConferenciaFinanceiraSubTab({ finance }) {
  const [loading, setLoading] = useState(false);
  const [pendencias, setPendencias] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Resolution States
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  // Bulk Resolution States
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkConfirmModalOpen, setIsBulkConfirmModalOpen] = useState(false);
  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  // Bulk Form Fields
  const [bulkContaBancaria, setBulkContaBancaria] = useState("");
  const [bulkFormaPagamento, setBulkFormaPagamento] = useState("");
  const [bulkStatus, setBulkStatus] = useState("pago");
  const [bulkCategoriaOverride, setBulkCategoriaOverride] = useState("");
  const [bulkCentroOverride, setBulkCentroOverride] = useState("");
  const [bulkFallbackCategoria, setBulkFallbackCategoria] = useState("");
  const [bulkFallbackCentro, setBulkFallbackCentro] = useState("");

  // Reconciliation States
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false);
  const [reconcileStartDate, setReconcileStartDate] = useState("");
  const [reconcileEndDate, setReconcileEndDate] = useState("");
  const [reconcileSources, setReconcileSources] = useState({
    production_orders: true,
    reservations: true,
    supply_purchases: true
  });
  const [reconciling, setReconciling] = useState(false);
  const [reconcileProgress, setReconcileProgress] = useState({ current: 0, total: 0, phase: "" });

  // Edit Form Fields
  const [editData, setEditData] = useState({
    data: "",
    descricao: "",
    valor: "",
    categoria: "",
    centro_custo: "",
    conta_bancaria: "",
    forma_pagamento: "",
    status: "pago",
    observacoes: ""
  });

  // Reject Form Field
  const [rejectReason, setRejectReason] = useState("");

  // Auto-initialize bulk bank account
  useEffect(() => {
    if (finance.contasBancarias?.length > 0 && !bulkContaBancaria) {
      setBulkContaBancaria(finance.contasBancarias[0].id);
    }
  }, [finance.contasBancarias, bulkContaBancaria]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pageIds = pendencias.map(p => p.id);
    const allSelected = pageIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
    }
  };

  const totals = useMemo(() => {
    let receitaSum = 0;
    let despesaSum = 0;
    selectedIds.forEach(id => {
      const p = pendencias.find(x => x.id === id);
      if (p) {
        const cents = p.valor_sugerido_centavos || 0;
        const isDespesa = cents < 0 || p.tipo_pendencia === "estorno";
        const val = Math.abs(cents);
        if (isDespesa) {
          despesaSum += val;
        } else {
          receitaSum += val;
        }
      }
    });
    return { receitaSum, despesaSum };
  }, [selectedIds, pendencias]);

  const fetchPendencias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await importacaoFinanceiraService.getPendenciasPaginated({
        page,
        perPage: 20,
        status: "pendente",
      });
      setPendencias(res.items || []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.totalItems || 0);
    } catch (err) {
      console.error("Erro ao carregar fila de conferência financeira:", err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPendencias();
  }, [fetchPendencias]);

  // Ação de confirmar direto
  const handleConfirmDirect = async (item) => {
    if (!item.categoria_sugerida || !item.centro_custo_sugerido) {
      alert("Para confirmar diretamente, o lançamento precisa possuir uma Categoria e um Centro de Custo mapeados. Use a opção de Editar.");
      return;
    }
    
    try {
      await finance.confirmarPendencia(item.id);
      await fetchPendencias();
    } catch (err) {
      alert("Erro ao confirmar pendência: " + err.message);
    }
  };

  // Abrir modal de Edição e Confirmação
  const openEditModal = (item) => {
    setSelectedItem(item);
    const isDespesa = item.valor_sugerido_centavos < 0 || item.tipo_pendencia === "estorno";
    setEditData({
      data: item.data_sugerida ? item.data_sugerida.split(" ")[0].split("T")[0] : "",
      descricao: item.descricao_sugerida || "",
      valor: formatCentavos(Math.abs(item.valor_sugerido_centavos || 0)).replace("R$", "").trim(),
      tipo_fluxo: isDespesa ? "despesa" : "receita",
      categoria: item.categoria_sugerida || "",
      centro_custo: item.centro_custo_sugerido || "",
      conta_bancaria: finance.contasBancarias[0]?.id || "",
      forma_pagamento: "",
      status: "pago",
      observacoes: item.snapshot?.motivo || ""
    });
    setIsEditModalOpen(true);
  };

  const handleEditConfirmSubmit = async (e) => {
    e.preventDefault();
    if (!editData.data || !editData.descricao || !editData.valor || !editData.categoria || !editData.centro_custo) {
      alert("Preencha todos os campos obrigatórios (*).");
      return;
    }

    // Identifica se é despesa ou receita
    const valueMultiplier = editData.tipo_fluxo === "despesa" ? -1 : 1;
    const cents = parseCentavos(editData.valor) * valueMultiplier;

    const payload = {
      data: editData.data,
      descricao: editData.descricao.trim(),
      valor_centavos: cents,
      categoria: editData.categoria,
      centro_custo: editData.centro_custo,
      conta_bancaria: editData.conta_bancaria || null,
      forma_pagamento: editData.forma_pagamento.trim() || null,
      status: editData.status,
      observacoes: editData.observacoes.trim() || null,
    };

    try {
      await finance.editarEConfirmarPendencia(selectedItem.id, payload);
      setIsEditModalOpen(false);
      await fetchPendencias();
    } catch (err) {
      alert("Erro ao editar e confirmar lançamento: " + err.message);
    }
  };
  // Abrir modal de Rejeição
  const openRejectModal = (item) => {
    setSelectedItem(item);
    setRejectReason("");
    setIsRejectModalOpen(true);
  };
  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) return;

    try {
      await finance.rejeitarPendencia(selectedItem.id, rejectReason.trim());
      setIsRejectModalOpen(false);
      await fetchPendencias();
    } catch (err) {
      alert("Erro ao rejeitar pendência: " + err.message);
    }
  };

  const handleBulkConfirm = async (e) => {
    e.preventDefault();
    if (!bulkContaBancaria) {
      alert("Selecione uma conta bancária para os lançamentos.");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    // Chunks of 10 concurrent requests to prevent PocketBase timeouts
    const chunkSize = 10;
    for (let i = 0; i < selectedIds.length; i += chunkSize) {
      const chunk = selectedIds.slice(i, i + chunkSize);
      await Promise.all(chunk.map(async (id) => {
        const item = pendencias.find(p => p.id === id);
        if (!item) return;

        const catId = bulkCategoriaOverride || item.categoria_sugerida || bulkFallbackCategoria;
        const centroId = bulkCentroOverride || item.centro_custo_sugerido || bulkFallbackCentro;

        if (!catId || !centroId) {
          failCount++;
          errors.push(`Item: ${item.descricao_sugerida} não possui Categoria/Centro.`);
          return;
        }

        const cents = item.valor_sugerido_centavos || 0;
        const isDespesa = cents < 0 || item.tipo_pendencia === "estorno";
        const valueMultiplier = isDespesa ? -1 : 1;
        const finalCents = Math.abs(cents) * valueMultiplier;

        // Formata os dados de confirmação da transação
        const editedData = {
          data: item.data_sugerida ? item.data_sugerida.split(" ")[0].split("T")[0] : new Date().toISOString().split("T")[0],
          descricao: item.descricao_sugerida,
          valor_centavos: finalCents,
          categoria: catId,
          centro_custo: centroId,
          conta_bancaria: bulkContaBancaria,
          forma_pagamento: bulkFormaPagamento,
          status: bulkStatus,
          observacoes: `[Consolidação em Lote] ${item.snapshot?.motivo || ""}`
        };

        try {
          await finance.editarEConfirmarPendencia(id, editedData);
          successCount++;
        } catch (err) {
          failCount++;
          errors.push(`Erro no Item ID ${id}: ${err.message}`);
        }
      }));
    }

    setLoading(false);
    setSelectedIds([]);
    setIsBulkConfirmModalOpen(false);
    await fetchPendencias();

    if (failCount > 0) {
      alert(`Consolidação concluída!\nSucessos: ${successCount}\nFalhas: ${failCount}\nErros:\n${errors.slice(0, 5).join("\n")}`);
    } else {
      alert(`Consolidação em lote concluída com sucesso! ${successCount} lançamentos registrados.`);
    }
  };

  const handleBulkReject = async (e) => {
    e.preventDefault();
    if (!bulkRejectReason.trim()) {
      alert("Por favor, digite o motivo da rejeição em lote.");
      return;
    }
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    // Chunk of 10 concurrent requests
    const chunkSize = 10;
    for (let i = 0; i < selectedIds.length; i += chunkSize) {
      const chunk = selectedIds.slice(i, i + chunkSize);
      await Promise.all(chunk.map(async (id) => {
        try {
          await finance.rejeitarPendencia(id, bulkRejectReason.trim());
          successCount++;
        } catch (err) {
          failCount++;
        }
      }));
    }

    setLoading(false);
    setSelectedIds([]);
    setBulkRejectReason("");
    setIsBulkRejectModalOpen(false);
    await fetchPendencias();
    alert(`Rejeição concluída! ${successCount} pendências rejeitadas, ${failCount} falhas.`);
  };

  const handleReconcileSubmit = async (e) => {
    e.preventDefault();
    if (!reconcileStartDate || !reconcileEndDate) {
      alert("Por favor, selecione as datas de início e fim.");
      return;
    }
    setReconciling(true);
    setReconcileProgress({ current: 0, total: 0, phase: "Iniciando varredura..." });
    try {
      const addedCount = await finance.reconcileExternalIntegrations(
        reconcileStartDate,
        reconcileEndDate,
        reconcileSources,
        setReconcileProgress
      );
      setIsReconcileModalOpen(false);
      await fetchPendencias();
      alert(`Varredura concluída!\nLançamentos importados para a fila de conferência: ${addedCount}`);
    } catch (err) {
      alert("Erro durante conciliação: " + err.message);
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      
      {/* Header Fila */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0 flex items-center justify-between shadow-sm">
        <div>
          <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
            <Inbox size={18} className="text-blue-600" /> Fila de Conferência Financeira
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Revise pendências vindas de integrações automáticas ou de planilhas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const today = new Date().toISOString().split("T")[0];
              const past = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
              setReconcileStartDate(past);
              setReconcileEndDate(today);
              setReconcileSources({
                production_orders: true,
                reservations: true,
                supply_purchases: true,
              });
              setIsReconcileModalOpen(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1 bg-slate-105 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded-lg text-xs font-black transition-colors shadow-sm"
          >
            <RefreshCw size={14} className={reconciling ? "animate-spin text-blue-600" : "text-slate-550"} />
            Conciliar Período
          </button>
          <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
            {totalItems} Pendentes
          </span>
        </div>
      </div>

      {/* Sub-Header / Ações em Massa */}
      {pendencias.length > 0 && (
        <div className="bg-slate-100 border-b border-slate-200 px-6 py-2.5 shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-inner">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={pendencias.length > 0 && pendencias.every(p => selectedIds.includes(p.id))}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-blue-500 cursor-pointer"
              />
              Selecionar Todos na Página
            </label>
            {selectedIds.length > 0 && (
              <span className="text-xs font-black text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded border border-blue-200 font-mono">
                {selectedIds.length} selecionados
              </span>
            )}
          </div>

          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-bold text-slate-500">
                Totais Selecionados: 
                <span className="text-emerald-600 font-mono font-black ml-1">+{formatCentavos(totals.receitaSum)}</span> 
                | 
                <span className="text-red-600 font-mono font-black ml-1">-{formatCentavos(totals.despesaSum)}</span>
              </span>
              <button
                onClick={() => {
                  setBulkRejectReason("");
                  setIsBulkRejectModalOpen(true);
                }}
                className="flex items-center gap-1 px-3 py-1 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 rounded-lg text-xs font-black transition-colors shadow-sm"
              >
                <X size={14} /> Rejeitar Lote
              </button>
              <button
                onClick={() => {
                  setBulkCategoriaOverride("");
                  setBulkCentroOverride("");
                  setBulkFallbackCategoria("");
                  setBulkFallbackCentro("");
                  setBulkFormaPagamento("");
                  setBulkStatus("pago");
                  setIsBulkConfirmModalOpen(true);
                }}
                className="flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-black transition-colors shadow-sm"
              >
                <Check size={14} /> Confirmar Lote
              </button>
            </div>
          )}
        </div>
      )}

      {/* Grade de Cards */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6 bg-slate-50">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
            <span className="text-xs font-semibold text-slate-400">Processando Fila...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl mx-auto w-full">
            {pendencias.map((item) => {
              const cents = item.valor_sugerido_centavos || 0;
              const isReceita = cents >= 0;
              return (
                <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                  <div>
                    {/* Header Card */}
                    <div className="flex justify-between items-start gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${
                          item.origem === "pdv_sales" ? "bg-purple-50 text-purple-700 border border-purple-200" :
                          item.origem === "production_orders" || item.origem === "producao_orders" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                          item.origem === "supply_purchases" ? "bg-red-50 text-red-700 border border-red-200" :
                          item.origem === "importacao_planilha" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                          item.origem === "colaboradores_historico_pagamentos" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                          item.origem === "pontuacao_pagamentos" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" :
                          item.origem === "motoboy_pagamentos" ? "bg-cyan-50 text-cyan-700 border border-cyan-200" :
                          "bg-slate-50 text-slate-600 border border-slate-200"
                        }`}>
                          {item.origem}
                        </span>
                      </div>
                      
                      <span className="text-[10px] font-bold text-slate-400 font-mono">
                        {item.data_sugerida ? item.data_sugerida.split(" ")[0].split("T")[0] : "-"}
                      </span>
                    </div>

                    {/* Conteúdo Card */}
                    <h3 className="text-sm font-black text-slate-800 tracking-wide line-clamp-1">{item.descricao_sugerida}</h3>
                    
                    {/* Valores e Taxonomias Sugeridas */}
                    <div className="grid grid-cols-2 gap-2 mt-4 text-[11px] border-t border-slate-100 pt-3">
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 block uppercase">Categoria Sugerida</span>
                        <span className="font-bold text-slate-700">
                          {item.expand?.categoria_sugerida?.nome || <span className="text-amber-600 font-medium">Não mapeada</span>}
                        </span>
                      </div>
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 block uppercase">Centro Custo</span>
                        <span className="font-bold text-slate-700">
                          {item.expand?.centro_custo_sugerido?.nome || <span className="text-amber-600 font-medium">Não mapeado</span>}
                        </span>
                      </div>
                    </div>

                    {/* Exibe snapshot da importação/origem (Invariante I4) */}
                    {item.snapshot && (
                      <div className="mt-3 bg-slate-50 border border-slate-100 rounded-lg p-2.5 text-[9px] font-semibold text-slate-500 font-mono space-y-0.5">
                        <div className="flex items-center gap-1 text-slate-400 border-b border-slate-100 pb-1 mb-1">
                          <Database size={12} /> DADOS ORIGINAIS DA INTEGRACAO
                        </div>
                        {item.snapshot.linha_planilha && <div>Linha da Planilha: #{item.snapshot.linha_planilha}</div>}
                        {item.snapshot.data_original && <div>Data Original: {item.snapshot.data_original}</div>}
                        {item.snapshot.valor_original && <div>Valor Original: {item.snapshot.valor_original}</div>}
                        {item.snapshot.categoria_original && <div>Cat Original: {item.snapshot.categoria_original}</div>}
                        {item.snapshot.centro_original && <div>Centro Original: {item.snapshot.centro_original}</div>}
                      </div>
                    )}
                  </div>

                  {/* Rodapé Ações Card */}
                  <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className={`text-sm font-black font-mono ${isReceita ? "text-emerald-700" : "text-rose-600"}`}>
                      {isReceita ? "+" : "-"} {formatCentavos(Math.abs(cents))}
                    </span>

                    <div className="flex gap-2">
                      <button
                        onClick={() => openRejectModal(item)}
                        className="flex items-center justify-center p-1.5 text-slate-400 hover:text-red-600 bg-slate-50 hover:bg-red-50 border border-slate-200 rounded-lg transition-colors"
                        title="Rejeitar Lançamento"
                      >
                        <X size={15} />
                      </button>
                      <button
                        onClick={() => openEditModal(item)}
                        className="flex items-center justify-center p-1.5 text-slate-400 hover:text-blue-600 bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-lg transition-colors"
                        title="Editar e Confirmar"
                      >
                        <Edit3 size={15} />
                      </button>
                      <button
                        onClick={() => handleConfirmDirect(item)}
                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[10px] font-bold shadow-sm transition-colors"
                        title="Confirmar Sem Modificações"
                      >
                        <Check size={14} /> Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {pendencias.length === 0 && (
              <div className="col-span-full bg-white border border-slate-200 rounded-xl py-16 flex flex-col items-center justify-center text-center shadow-sm">
                <Check size={40} className="text-emerald-500 mb-3" />
                <h3 className="font-bold text-sm text-slate-800 uppercase tracking-wide">Fila Limpa!</h3>
                <p className="text-xs text-slate-400 mt-1">Nenhum lançamento pendente aguardando conferência.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Barra de Paginação */}
      <div className="bg-slate-50 border-t border-slate-200 p-3 shrink-0 flex items-center justify-between">
        <span className="text-xs text-slate-500 font-bold">
          Total de pendências: {totalItems}
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1 border border-slate-200 rounded bg-white hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <ChevronLeft size={16} />
          </button>
          
          <span className="text-xs font-bold text-slate-700 font-mono">
            {page} / {totalPages}
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1 border border-slate-200 rounded bg-white hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ─── MODAL: EDITAR E CONFIRMAR ─────────────────────────────────── */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Editar e Confirmar Lançamento</h4>
              <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleEditConfirmSubmit} className="p-5 flex-1 space-y-4 max-h-[80vh] overflow-y-auto">
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data *</label>
                  <input
                    type="date"
                    required
                    value={editData.data}
                    onChange={(e) => setEditData({ ...editData, data: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo de Fluxo *</label>
                  <select
                    value={editData.tipo_fluxo}
                    onChange={(e) => setEditData({ ...editData, tipo_fluxo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="receita">Receita (Entrada)</option>
                    <option value="despesa">Despesa (Saída)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descrição / Histórico *</label>
                <input
                  type="text"
                  required
                  value={editData.descricao}
                  onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Valor (R$) *</label>
                  <input
                    type="text"
                    required
                    value={editData.valor}
                    onChange={(e) => setEditData({ ...editData, valor: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold font-mono text-right"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Status Lançamento *</label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="pago">Pago / Confirmado</option>
                    <option value="a_vencer">A Vencer</option>
                    <option value="provisao">Provisão</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                  <select
                    required
                    value={editData.categoria}
                    onChange={(e) => setEditData({ ...editData, categoria: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="" disabled>Selecione...</option>
                    {finance.categorias
                      .filter((c) => c.ativo && c.tipo === editData.tipo_fluxo)
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Centro de Custo *</label>
                  <select
                    required
                    value={editData.centro_custo}
                    onChange={(e) => setEditData({ ...editData, centro_custo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="" disabled>Selecione...</option>
                    {finance.centrosCusto.filter(c => c.ativo).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Conta Bancária</label>
                  <select
                    value={editData.conta_bancaria}
                    onChange={(e) => setEditData({ ...editData, conta_bancaria: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="">Nenhuma...</option>
                    {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                  <input
                    type="text"
                    value={editData.forma_pagamento}
                    onChange={(e) => setEditData({ ...editData, forma_pagamento: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Pix, Cartão, Dinheiro..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Anotações Internas (Ledger)</label>
                <textarea
                  value={editData.observacoes}
                  onChange={(e) => setEditData({ ...editData, observacoes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Informações adicionais para auditoria..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Confirmar Lançamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: REJEITAR PENDÊNCIA ─────────────────────────────────── */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Rejeitar Pendência Financeira</h4>
              <button onClick={() => setIsRejectModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleRejectSubmit} className="p-5 space-y-4">
              <div className="flex gap-2 bg-red-50 border border-red-200 text-red-900 p-3 rounded-lg text-xs font-semibold">
                <AlertCircle size={20} className="shrink-0 text-red-650 mt-0.5" />
                <div>
                  <strong>Atenção:</strong> Rejeitar um lançamento pendente impede que ele entre no ledger final, mas o registro original permanecerá arquivado como rejeitado com motivo (Invariante I6).
                </div>
              </div>

              <div>
                <span className="text-xs font-bold text-slate-400 block mb-1">Lançamento a Rejeitar:</span>
                <span className="text-xs font-black text-slate-700 block mb-4">{selectedItem?.descricao_sugerida}</span>

                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Motivo da Rejeição *</label>
                <input
                  type="text"
                  required
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="EX: Lançamento duplicado, valor incorreto na planilha"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsRejectModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <X size={16} /> Rejeitar Lançamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: CONFIRMAÇÃO EM MASSA (LOTE) ─────────────────────────── */}
      {isBulkConfirmModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Consolidar Lançamentos em Massa</h4>
              <button onClick={() => setIsBulkConfirmModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleBulkConfirm} className="p-5 flex-1 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded-lg text-xs font-semibold">
                Você selecionou <strong>{selectedIds.length}</strong> lançamentos para consolidar de uma vez.
                <div className="mt-1">
                  Total Receitas: <strong className="text-emerald-700">+{formatCentavos(totals.receitaSum)}</strong> |
                  Total Despesas: <strong className="text-red-700">-{formatCentavos(totals.despesaSum)}</strong>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Conta Bancária *</label>
                  <select
                    required
                    value={bulkContaBancaria}
                    onChange={(e) => setBulkContaBancaria(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="" disabled>Selecione...</option>
                    {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                  <input
                    type="text"
                    value={bulkFormaPagamento}
                    onChange={(e) => setBulkFormaPagamento(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Pix, Cartão, Dinheiro..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Status de Liquidação *</label>
                  <select
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="pago">Pago / Recebido</option>
                    <option value="a_vencer">A Vencer / A Receber</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3 mt-2">
                <span className="text-xs font-black text-slate-700 uppercase tracking-wide block mb-2">Classificação dos Lançamentos</span>
                
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-3">
                  <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                      Sobrescrever TODAS as Categorias
                    </label>
                    <select
                      value={bulkCategoriaOverride}
                      onChange={(e) => setBulkCategoriaOverride(e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      <option value="">Manter sugestões de cada item (Recomendado)</option>
                      {finance.categorias.filter(c => c.ativo).map((c) => (
                        <option key={c.id} value={c.id}>[{c.tipo.toUpperCase()}] {c.nome}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                      Sobrescrever TODOS os Centros de Custo
                    </label>
                    <select
                      value={bulkCentroOverride}
                      onChange={(e) => setBulkCentroOverride(e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                    >
                      <option value="">Manter sugestões de cada item (Recomendado)</option>
                      {finance.centrosCusto.filter(c => c.ativo).map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>

                  {!bulkCategoriaOverride && (
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                        Categoria Fallback (Se o item não tiver sugestão)
                      </label>
                      <select
                        value={bulkFallbackCategoria}
                        onChange={(e) => setBulkFallbackCategoria(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      >
                        <option value="">Selecione...</option>
                        {finance.categorias.filter(c => c.ativo).map((c) => (
                          <option key={c.id} value={c.id}>[{c.tipo.toUpperCase()}] {c.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!bulkCentroOverride && (
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                        Centro Fallback (Se o item não tiver sugestão)
                      </label>
                      <select
                        value={bulkFallbackCentro}
                        onChange={(e) => setBulkFallbackCentro(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      >
                        <option value="">Selecione...</option>
                        {finance.centrosCusto.filter(c => c.ativo).map((c) => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsBulkConfirmModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Confirmar {selectedIds.length} Itens
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: REJEIÇÃO EM MASSA (LOTE) ─────────────────────────────── */}
      {isBulkRejectModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Rejeitar Pendências em Massa</h4>
              <button onClick={() => setIsBulkRejectModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleBulkReject} className="p-5 space-y-4">
              <div className="flex gap-2 bg-red-50 border border-red-200 text-red-900 p-3 rounded-lg text-xs font-semibold">
                <AlertCircle size={20} className="shrink-0 text-red-650 mt-0.5" />
                <div>
                  <strong>Atenção:</strong> Você está rejeitando <strong>{selectedIds.length}</strong> lançamentos selecionados. Eles não entrarão no ledger final.
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Motivo da Rejeição em Lote *</label>
                <input
                  type="text"
                  required
                  value={bulkRejectReason}
                  onChange={(e) => setBulkRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="EX: Lote de importação duplicado ou incorreto"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsBulkRejectModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <X size={16} /> Rejeitar {selectedIds.length} Lançamentos
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ─── MODAL: CONCILIAÇÃO POR PERÍODO (VARREDURA MANUAL) ─────────── */}
      {isReconcileModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide flex items-center gap-1.5">
                <RefreshCw size={16} className={reconciling ? "animate-spin" : ""} /> Conciliar Período
              </h4>
              <button onClick={() => setIsReconcileModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleReconcileSubmit} className="p-5 space-y-4">
              <div className="bg-slate-50 border border-slate-200 text-slate-700 p-3 rounded-lg text-xs font-semibold space-y-1">
                <strong>O que é isso?</strong>
                <div>Varre o banco de dados buscando registros operacionais dentro do período selecionado que ainda não foram lançados no financeiro e traz apenas os faltantes para a fila de conferência.</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data Inicial *</label>
                  <input
                    type="date"
                    required
                    value={reconcileStartDate}
                    onChange={(e) => setReconcileStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data Final *</label>
                  <input
                    type="date"
                    required
                    value={reconcileEndDate}
                    onChange={(e) => setReconcileEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-600 uppercase mb-2">Origens para Varredura</label>
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg space-y-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={reconcileSources.production_orders}
                      onChange={(e) => setReconcileSources({ ...reconcileSources, production_orders: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-blue-500"
                    />
                    Pedidos de Fábrica (production_orders)
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={reconcileSources.reservations}
                      onChange={(e) => setReconcileSources({ ...reconcileSources, reservations: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-blue-500"
                    />
                    Reservas de E-commerce / PDV (reservations)
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={reconcileSources.supply_purchases}
                      onChange={(e) => setReconcileSources({ ...reconcileSources, supply_purchases: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-blue-500"
                    />
                    Compras de Insumo (supply_purchases)
                  </label>
                </div>
              </div>

              {reconciling && (
                <div className="mt-4 space-y-2 p-3 bg-slate-50 border border-slate-200 rounded-lg animate-fade-in">
                  <div className="flex justify-between items-center text-[10px] font-black text-slate-500 uppercase tracking-wide">
                    <span className="truncate max-w-[220px]">{reconcileProgress.phase}</span>
                    {reconcileProgress.total > 0 && (
                      <span>
                        {Math.round((reconcileProgress.current / reconcileProgress.total) * 100)}%
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden shadow-inner">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                      style={{
                        width: `${reconcileProgress.total > 0 ? (reconcileProgress.current / reconcileProgress.total) * 100 : 10}%`
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsReconcileModalOpen(false)}
                  disabled={reconciling}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={reconciling}
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors disabled:opacity-50"
                >
                  {reconciling ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" /> Processando...
                    </>
                  ) : (
                    <>
                      <Check size={16} /> Iniciar Varredura
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
